import { memo, useEffect, useState, useRef, useMemo } from 'react';
import { CanPayItem, ISubjectsOfCanPayItem } from '@/types';
import classNames from 'classnames';

type IItemType = 'single_level' | 'multi_level';

interface IApplicableData {
    itemType: IItemType;
    title: string;
    list: Array<{ id: number; name: string; list?: Array<{ id: number; name: string }> }>;
}

interface IProps {
    canPayList: CanPayItem[];
    noData: boolean;
    productId?: number;
    categoryTitles: string[];
    onProductChange: (data: CanPayItem) => void;
}

const RightsList = ({ canPayList, noData, productId, categoryTitles, onProductChange }: IProps) => {
    const [isPayScroll, setPayScroll] = useState(false);
    const [applicableData, setApplicableData] = useState<IApplicableData[]>([]);
    const listRef = useRef<any>(null);

    const containerStyle = useMemo(() => {
        return {
            height: `${categoryTitles.length * 60 + 20}px`,
            backgroundSize: `100% ${categoryTitles.length * 60 + 20}px`,
        };
    }, [categoryTitles]);

    const handlePayScroll = e => {
        const right = e.target.scrollLeft;
        setPayScroll(right > 8 ? true : false);
    };

    const transformToApplicableDataItem = (title: string, list: ISubjectsOfCanPayItem) => {
        let itemType: IItemType = 'single_level';
        const newList = list.map(item => {
            if (item.tlSubjects?.length) {
                itemType = 'multi_level';
                return { id: item.id, name: item.name.length > 6 ? `${item.name.slice(0, 6)}...` : item.name, list: item.tlSubjects };
            }
            return { id: item.id, name: item.name, list: item.tlSubjects };
        });
        return { itemType, title, list: newList };
    };

    const handleSwitchRight = (data: CanPayItem, index: number, init = false) => {
        const applicableData: IApplicableData[] = [];
        if (data.lucaSubjects?.length) {
            applicableData.push(transformToApplicableDataItem('luca答疑', data.lucaSubjects));
        }
        if (data.teacherSubjects?.length) {
            applicableData.push(transformToApplicableDataItem('名师答疑', data.teacherSubjects));
        }
        if (data.aiCorrectSubjects?.length) {
            applicableData.push(transformToApplicableDataItem('智能批改', data.aiCorrectSubjects));
        }
        if (data.manualCorrectSubjects?.length) {
            applicableData.push(transformToApplicableDataItem('人工批改', data.manualCorrectSubjects));
        }
        setApplicableData(applicableData);
        if (!init) {
            const dom = listRef.current.children[index] as HTMLDivElement;
            dom.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'end' });
            onProductChange(data);
        }
    };

    useEffect(() => {
        if (canPayList.length) {
            handleSwitchRight(canPayList[0], 0, true);
        } else {
            setApplicableData([]);
            setPayScroll(false);
        }
    }, [canPayList]);

    return (
        <div className="rights-pay-box">
            {!!canPayList.length && (
                <>
                    <div className="rights-top">
                        {canPayList.map((item, index) => (
                            <div
                                key={item.id}
                                onClick={() => handleSwitchRight(item, index)}
                                className={classNames('rights-money-item', {
                                    active: productId === item.productId,
                                })}
                            >
                                <div>{item.equityTitle.substring(0, 6)}</div>
                                <div>
                                    ¥<span style={{ fontSize: String(item.amount).length > 5 ? 28 : 32 }}>{item.amount}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="rights-bottom-container" style={containerStyle}>
                        <div className="rights-bottom">
                            <div
                                className={classNames('rights-head', {
                                    'handle-scroll': isPayScroll,
                                })}
                            >
                                {categoryTitles.map(item => (
                                    <div key={item}>{item}</div>
                                ))}
                            </div>
                            <div className="rights-item-box" onScroll={handlePayScroll} ref={listRef}>
                                {canPayList.map(item => (
                                    <div
                                        key={item.id}
                                        className={classNames('rights-item', {
                                            active: item.productId === productId,
                                        })}
                                    >
                                        <div>{item.equityTitle.substring(0, 6)}</div>
                                        <div>{item.lucaAskNumStr}</div>
                                        <div>{item.teacherAskNumStr}</div>
                                        {item.aiCorrectNumStr && <div>{item.aiCorrectNumStr}</div>}
                                        {item.manualCorrectNumStr && <div>{item.manualCorrectNumStr}</div>}
                                        <div>{item.validityTimeStr}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {applicableData.length > 0 && (
                        <div className="rights-applicable-info">
                            <div className="rights-applicable-title">*适用于以下科目</div>
                            {applicableData.map(item => (
                                <div key={item.title} className="rights-applicable-container">
                                    <div className="rights-applicable-container__title">{item.title}</div>
                                    {item.itemType === 'single_level' ? (
                                        <div className="rights-applicable-container__single-container">
                                            {item.list.map(childItem => (
                                                <div className="rights-applicable-container__single-item" key={childItem.id}>
                                                    {childItem.name}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rights-applicable-container__multi-container">
                                            {item.list.map((childItem, index) => (
                                                <div
                                                    key={childItem.id}
                                                    className={classNames(
                                                        'rights-applicable-container__multi-item',
                                                        index % 2 && 'rights-applicable-container__multi-item--even'
                                                    )}
                                                >
                                                    <span className="rights-applicable-container__subject-name">{childItem.name}</span>
                                                    {!!childItem.list?.length && (
                                                        <div className="rights-applicable-container__two-level-subject">
                                                            {childItem.list.map(childItem2 => (
                                                                <div
                                                                    className="rights-applicable-container__two-level-subject-name"
                                                                    key={childItem2.id}
                                                                >
                                                                    {childItem2.name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
            {noData && (
                <div className="empty-box">
                    <img src="http://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202406/06/619d2_20240606093432.png" />
                </div>
            )}
            <div className="rights-pay-box__bottom-placeholder"></div>
        </div>
    );
};

export default memo(RightsList);
